import React, { useState } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadBox = styled(Box)(({ theme }) => ({
  border: '2px dashed #d3dbe6',
  borderRadius: '12px',
  background: '#f7fafd',
  minHeight: '180px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: theme.spacing(4),
  position: 'relative',
}));

interface EvidenceProps {
  uploadedFiles: File[];
  onFilesUpload: (files: File[]) => void;
}

const MAX_FILES = 5;
const ACCEPTED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];
const ACCEPTED_DOC_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const ACCEPTED_TYPES = [...ACCEPTED_PHOTO_TYPES, ...ACCEPTED_DOC_TYPES];

const Evidence: React.FC<EvidenceProps> = ({
  uploadedFiles,
  onFilesUpload
}) => {
  const [files, setFiles] = useState<File[]>(uploadedFiles || []);
  const [error, setError] = useState<string>('');

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files || []);
    const totalFiles = files.length + newFiles.length;
    if (totalFiles > MAX_FILES) {
      setError(`You can upload up to ${MAX_FILES} files.`);
      return;
    }
    // Filter accepted types
    const validFiles = newFiles.filter(file => ACCEPTED_TYPES.includes(file.type));
    if (validFiles.length !== newFiles.length) {
      setError('Some files were not accepted. Only images (JPG, PNG) and documents (PDF, DOC, DOCX) are allowed.');
      return;
    }
    setFiles([...files, ...validFiles]);
    onFilesUpload([...files, ...validFiles]);
    setError('');
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
    onFilesUpload(newFiles);
    setError('');
  };

  return (
    <UploadBox
      id="evidence_upload-container"
      data-test-id="test-evidence_upload-container"
      aria-label="Evidence upload section"
    >
      <CloudUploadIcon 
        sx={{ fontSize: 40, color: '#7b8db0', mb: 1 }}
        id="evidence_upload-icon"
        data-test-id="test-evidence_upload-icon"
        aria-label="Upload icon"
      />
      <Typography 
        variant="subtitle1" 
        sx={{ fontWeight: 600, fontFamily: 'var(--font-noto-sans)' }}
        id="evidence_upload-title"
        data-test-id="test-evidence_upload-title"
        aria-label="Upload related documents"
      >
        Upload any other related documents
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ color: '#7b8db0', mb: 2, fontFamily: 'var(--font-noto-sans)' }}
        id="evidence_upload-description"
        data-test-id="test-evidence_upload-description"
        aria-label="Upload instructions for photos and documents"
      >
        Upload up to 5 photos (JPG, PNG) or documents (PDF, DOC, DOCX) showing property damage
      </Typography>
      <label htmlFor="evidence-upload">
        <input
          accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
          style={{ display: 'none' }}
          id="evidence-upload"
          data-test-id="test-evidence_upload-input"
          aria-label="File upload input"
          multiple
          type="file"
          onChange={handleFileUpload}
        />
        <Button
          variant="outlined"
          component="span"
          startIcon={<CloudUploadIcon />}
          sx={{ fontFamily: 'var(--font-noto-sans)' }}
          disabled={files.length >= MAX_FILES}
          id="evidence_upload-button"
          data-test-id="test-evidence_upload-button"
          aria-label="Click to upload files"
        >
          Upload Files
        </Button>
      </label>
      <Typography 
        variant="body2" 
        sx={{ mt: 2, fontFamily: 'var(--font-noto-sans)' }}
        id="evidence_upload-count"
        data-test-id="test-evidence_upload-count"
        aria-label={`${files.length} out of ${MAX_FILES} files uploaded`}
      >
        {files.length}/{MAX_FILES} files
      </Typography>
      {error && (
        <Typography 
          color="error" 
          variant="caption" 
          sx={{ mt: 1, fontFamily: 'var(--font-noto-sans)' }}
          id="evidence_upload-error"
          data-test-id="test-evidence_upload-error"
          aria-label="Upload error message"
        >
          {error}
        </Typography>
      )}
      {files.length > 0 && (
        <Box 
          sx={{ mt: 2, width: '100%' }}
          id="evidence_uploaded-files"
          data-test-id="test-evidence_uploaded-files"
          aria-label="List of uploaded files"
        >
          {files.map((file, idx) => (
            <Box 
              key={idx} 
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}
              id={`evidence_uploaded-file-${file.name}`}
              data-test-id={`test-evidence_uploaded-file-${file.name}`}
              aria-label={`Uploaded file ${file.name}`}
            >
              <Typography 
                variant="body2" 
                sx={{ fontFamily: 'var(--font-noto-sans)' }}
                id={`evidence_uploaded-file-name-${file.name}`}
                data-test-id={`test-evidence_uploaded-file-name-${file.name}`}
                aria-label={`File name: ${file.name}`}
              >
                {file.name}
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => handleRemoveFile(idx)}
                id={`evidence_remove-file-${file.name}`}
                data-test-id={`test-evidence_remove-file-${file.name}`}
                aria-label={`Remove file ${file.name}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </UploadBox>
  );
};

export default Evidence; 